import React, { useState, useEffect, useRef } from "react";
import "./Header.scss";
import { IconButton, TextField, InputAdornment, Dialog } from "@mui/material";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import {
  errorModel,
  IMasterData,
  MasterDataItem,
  IHeaderProps,
  INavigationData,
  NavigationItems,
} from "../../context/model";
import { performSearch } from "../../services/searchquery"; // Updated import
import { useAppContext } from "../../provider/AppContextProvider";
import { useQuery } from "@apollo/client/react";
import { HEADER_FOOTER_QUERY, NAVIGATION_QUERY } from "../../services/query";
import { logger } from "../../services/logger";
import { REACT_APP_WUCA_DXP_ADMIN_URL } from "../../services/config";
import { getLocalizedValueSync } from "../../services/localizationService";
import { useGlobalContext } from "../../provider/GlobalContextProvider";

function Header({ header }: IHeaderProps) {
  const { userName, roleName,dashboardListId } = useAppContext();
  if (userName || userName !== "") {
    header.userName = userName;
  }
  if (roleName || roleName !== "") {
    header.userRole = roleName;
  }
  const localizedHeaderSearch = getLocalizedValueSync("CA.Header.Search") || "";

  const navigate = useNavigate();
  const location = useLocation();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [open, setOpen] = React.useState(false);

  const searchRef = useRef<HTMLDivElement>(null);
  const { updateGLoading } = useGlobalContext();
  const { updateLoading, isInitializing } = useAppContext();
  const [headerItem, setHeader] = useState<MasterDataItem[]>([]);
  const {
    error: errorHeader,
    data: dataHeader,
    loading: loadingHeader,
  } = useQuery<IMasterData>(HEADER_FOOTER_QUERY, {
    skip: isInitializing,
  });

  const [navigation, setNavigation] = useState<NavigationItems[]>([]);
  const validDashboardIds = dashboardListId && dashboardListId.length > 0 ? dashboardListId : ['default-dashboard-id'];
  const {
    error: errorNavigation,
    data: dataNavigation,
    loading: loadingNavigation,
  } = useQuery<INavigationData>(NAVIGATION_QUERY, {
    skip: isInitializing|| !dashboardListId || dashboardListId.length === 0,
    variables: { dashboardIds: validDashboardIds },
  });

  useEffect(() => {
    if (dataNavigation) {
      const activeItems = dataNavigation.cANavigationCollection.items
        .filter((item) => item.active &&
          (item.dashboard?.items ?? []).some(d =>
            d._system?.id && validDashboardIds.includes(d._system.id)
          )
        )
        .sort((a, b) => a.sortOrder - b.sortOrder);
      setNavigation(activeItems);
    }

    if (errorNavigation) {
      updateLoading(false, "navigationLoading");
      setNavigation([]);
      const errorModel: errorModel = {
        type: "Error",
        source: "Header.tsx",
        error: `${errorNavigation.message}\n${errorNavigation.stack} \n${errorNavigation.name}`,
        eventCode: "Header",
      };
      logger.logToApi(errorModel);
      logger.logToAppInsights(errorNavigation as Error, 3);
    }
  }, [dataNavigation, errorNavigation]);

  useEffect(() => {
    updateLoading(loadingNavigation, "navigationLoading");
  }, [loadingNavigation]);

  const sanitizeUrl = (url: string) => {
    return url
      .trim()
      .replace(/[^a-zA-Z0-9-]/g, " ")
      .replace(/\s+/g, "-")
      .toLocaleLowerCase();
  };

  const renderNavLink = (item: NavigationItems, index: number) => {
    const isDashboardActive =
      location.pathname === "/" || location.pathname === "/dashboard";
    const isActiveLink =
      isDashboardActive && item.title.toLowerCase() === "dashboard";

    if (item.isExternal) {
      return (
        <a key={index} target="_blank" href={item.url} className="nav-link">
          {item.title}
        </a>
      );
    } else {
      return (
        <NavLink
          key={index}
          to={sanitizeUrl(item.title)}
          onClick={() => setIsOpen(false)}
          className={({ isActive }) =>
            `nav-link ${isActive || isActiveLink ? "active-link" : ""}`
          }
        >
          {item.title}
        </NavLink>
      );
    }
  };

  useEffect(() => {
    if (dataHeader) {
      setHeader(dataHeader?.cAMasterDataCollection.items);
    }

    if (errorHeader) {
      updateLoading(false, "headerLoading");
      setHeader([]);
    }
  }, [dataHeader, errorHeader]);

  useEffect(() => {
    updateLoading(loadingHeader, "headerLoading");
    updateGLoading(false, "globalHeaderLoading");
  }, [loadingHeader]);

  useEffect(() => {
    if (location.pathname !== "/search-results") {
      setSearchKeyword(""); // Clear the search keyword if not on search-results page
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleSearch = async () => {
    if (searchKeyword.trim() === "") return; // Prevent storing blank keywords
    const results = await performSearch(searchKeyword);
    setShowDropdown(false); // Hide dropdown after search
    setOpen(false); // Close search dialog after search
    navigate("/search-results", { state: { results, keyword: searchKeyword } });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
      setOpen(false); // Close search dialog after search
    }
  };

  const handleBackClick = () => {
    setSearchKeyword(""); // Clear the search keyword
    setShowDropdown(false);
  };

  const toPascalCase = (str: string) => {
    return str.replace(
      /\w+/g,
      (w) => w[0].toUpperCase() + w.slice(1).toLowerCase()
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const pascalCaseValue = toPascalCase(inputValue);
    setSearchKeyword(pascalCaseValue);
    setShowDropdown(false); // Hide dropdown when typing
  };

  const handleInputClick = () => {
    if (searchKeyword === "") {
      setShowDropdown(true); // Show dropdown on click if input is empty
    }
  };

  const handleClickOpen = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    setShowDropdown(true);
  };
  const navigationElement = () => {
    return (
      <div className="top-navigation">
        <div className="navigation-links">
          {navigation.map((item, index) => renderNavLink(item, index))}
        </div>
      </div>
    );
  };
  const searchElement = () => {
    return (
      <div className="search" ref={searchRef}>
        <TextField
          className="search-input"
          placeholder="Keyword Search"
          value={searchKeyword}
          onChange={handleInputChange} // Handle input change
          onClick={handleInputClick} // Show dropdown on click
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <span
                  className="fa-kit fa-arrow-left"
                  onClick={handleBackClick}
                ></span>
              </InputAdornment>
            ),
            sx: {
              height: "40px",
              backgroundColor: "#fff",
              borderRadius: "4px 0 0 4px",
              "& fieldset": { border: "none" },
            },
          }}
          sx={{ flexGrow: 1 }}
        />
        <button onClick={handleSearch}>{localizedHeaderSearch}</button>
      </div>
    );
  };

  return (
    <>
      {headerItem.map((item, index) => (
        <div className="header-container" key={index}>
          <div className="header">
            <div
              className="logo"
              onClick={handleLogoClick}
              style={{ cursor: "pointer" }}
            >
              <img
                src={`${REACT_APP_WUCA_DXP_ADMIN_URL}${item?.imageContentHub?.items?.[0]?.contentFile?.path}`}
                alt="Logo"
              />
            </div>
            <div className="dashboard-search">{searchElement()}</div>
            <div className="mobile-header-icons">
              <button
                className="mobile-header-search-icon"
                onClick={() => setOpen(!open)}
              >
                <span className="fa-kit fa-magnify"></span>
              </button>
              <button
                className="mobile-header-menu-toggle"
                onClick={() => setIsOpen(!isOpen)}
                disabled={open}
              >
                <span className="fa-kit fa-menu"></span>
              </button>
            </div>
            {isOpen && (
              <nav className="mobile-nav">
                <button
                  className="mobile-nav-close-btn"
                  onClick={() => setIsOpen(false)}
                >
                  <span className="fa-kit fa-times"></span>
                </button>
                {navigationElement()}
              </nav>
            )}
          </div>
          <div className="user-profile">
            <div className="user-details">
              <div className="user-name">{header.userName}</div>
              <div className="user-role">{header.userRole}</div>
            </div>
          </div>
        </div>
      ))}
      <div className="dashboard-navigation">{navigationElement()}</div>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        className="search-dialog"
        disableEnforceFocus
        disableEscapeKeyDown
      >
        <div className="dialog-search">{searchElement()}</div>
      </Dialog>
    </>
  );
}
export default Header;
